import React, { useEffect, useRef, useState } from "react";
import { MDialog, MSpinner } from "components/Shared";
import Button from "@mui/material/Button";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { GenerateErrorReportRequest } from "interfaces/Mueller.Business.Models";
import { generateErrorReport } from "requests/phoenixForms";
import moment from "moment";

type CommonProps = {
  formName: string;
  onDismiss: () => void;
}

// Using a discriminated union for props to ensure only one of phoenixFormId or availFormId is provided
type Props = CommonProps & (
  | { phoenixFormId: string; availFormId?: never; mode: "phoenix" }
  | { availFormId: number; phoenixFormId?: never; mode: "availform" }
)

enum DialogState { ENTERING, SUBMITTING, SUCCESS, ERROR }

const GenerateErrorReportDialog = ({
  formName,
  phoenixFormId,
  availFormId,
  mode,
  onDismiss
} : Props) => {

  const mounted = useRef<boolean>(true);
  const [startDate, setStartDate] = useState<Date>(null);
  const [endDate, setEndDate] = useState<Date>(null);
  const [errorText, setErrorText] = useState<string>(null);
  const [dialogState, setDialogState] = useState<DialogState>(DialogState.ENTERING);

  useEffect(() => () => { mounted.current = false; }, []);

  useEffect(() => setErrorText(null), [startDate, endDate]);

  const onSubmit = () => {
    if (!startDate || !endDate) {
      setErrorText("Must select a start and end date");
      return;
    }
    const startMoment = moment(startDate);
    const endMoment = moment(endDate);
    const diffDays = endMoment.diff(startMoment, "days");
    if (diffDays > 7) {
      setErrorText("Date range can be a maximum of 7 days");
      return;
    }

    const request: GenerateErrorReportRequest = {
      phoenixFormId: undefined, // lint is being stupid
      availFormId: undefined, // lint is being stupid
      startDate: startDate,
      endDate: endDate,
    };

    if (mode === "phoenix" && phoenixFormId) {
      request.phoenixFormId = phoenixFormId;
    } else if (mode === "availform" && availFormId) {
      request.availFormId = availFormId;
    } else {
      return; // shouldn't happen, but won't send a bad request
    }
    
    setDialogState(DialogState.SUBMITTING);
    generateErrorReport(
      request,
      () => {
        setDialogState(DialogState.SUCCESS);
      },
      () => {
        setDialogState(DialogState.ERROR);
      },
      mounted
    );
  };

  const buttons = <>
    {dialogState === DialogState.ENTERING &&
    <>
      <Button onClick={onDismiss}
      >Cancel</Button>
      <Button
        style={{marginLeft: 10}}
        onClick={onSubmit}
      >Submit</Button>
    </>
    }
    {(dialogState === DialogState.SUCCESS || dialogState === DialogState.ERROR) &&
      <Button
        onClick={onDismiss}
      >OK</Button>
    }
  </>;

  return (
    <MDialog
      id="generate-error-report-dialog"
      title="Generate Error Report"
      visible
      dontSpecifyWidth
      contentActions={buttons}
    >
      {dialogState === DialogState.ENTERING &&
        <div>
          <h2>{formName}</h2>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <div>
              <DatePicker
                label="Start Date:"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                slotProps={{ textField: { variant: 'standard' } }}
              />
            </div>
            <div>
              <DatePicker
                label="End Date:"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                slotProps={{ textField: { variant: 'standard' } }}
              />
            </div>
          </LocalizationProvider>
          <p className="validation-error">{errorText}</p>
        </div>
      }
      {dialogState === DialogState.SUBMITTING &&
        <MSpinner />
      }
      {dialogState === DialogState.SUCCESS &&
        <div>
          <p>The error report was queued for generation.</p>
          <p>You will receive an email once the process is completed.</p>
        </div>
      }
      {dialogState === DialogState.ERROR &&
        <p>Error generating error report. Oh the irony.</p>
      }
    </MDialog>
  );
};

export default GenerateErrorReportDialog;
